// OWNER: FRONTEND-DIALER agent. SPEC §15.4: upcoming and past meetings; admins mark held / no-show with a note;
// setters may cancel their own; show rate by setter for the loaded range. Booked calls (migration 0027): each meeting
// with a booked call opens its player, closer brief and transcript; /meetings?open=<id> (the Discord link) opens one.
// Admins (PJ 2026-10-20): "Calendly bookings to link", the bookings made on a Calendly link that the webhook couldn't
// match to one business (30 days), each linked by hand to the business picked here.
import '../dialer/dialer.css';
import { useEffect, useState } from 'preact/hooks';
import type { Meeting, MeetingStatus } from '../../../src/shared/api';
import type { CalendlyUnlinkedBooking, UserAdmin } from '../../../src/shared/api-core';
import type { BookedCallDetail, MeetingWithBookedCall } from '../../../src/shared/api-booked-calls';
import { api, errorMessage } from '../api';
import { BookedCallPanel } from '../components/BookedCall';
import { Empty, ErrorBanner, fmtDateTime, pct, toast } from '../components/ui';
import { ConfirmButton, isoDay, LinkOut, Segmented, Spinner, useAsync } from '../components/ui-extra';
import { endpoints } from '../dialer/endpoints';
import { LinkBusiness } from '../dialer/LinkBusiness';
import { bookedAgo, bookingWho } from '../dialer/linked';
import { useMe } from '../me';

/** PATCH responses are expected to be the Meeting; anything else falls back to the local edit. */
function asMeeting(v: unknown, fallback: Meeting): Meeting {
  return v && typeof v === 'object' && typeof (v as Meeting).id === 'number' && typeof (v as Meeting).status === 'string' ? (v as Meeting) : fallback;
}

const STATUS_TONE: Record<MeetingStatus, string> = { booked: 'chip-info', held: 'chip-ok', no_show: 'chip-bad', rescheduled: 'chip-warn', canceled: '' };
const STATUS_LABEL: Record<MeetingStatus, string> = { booked: 'Booked', held: 'Held', no_show: 'No-show', rescheduled: 'Rescheduled', canceled: 'Canceled' };

function Outcome(props: { m: Meeting; onSaved: (m: Meeting) => void }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(props.m.outcomeNote ?? '');
  const [busy, setBusy] = useState(false);
  const save = async (status: MeetingStatus) => {
    setBusy(true);
    try {
      const updated = await endpoints.updateMeeting(props.m.id, { status, outcomeNote: note.trim() || undefined });
      props.onSaved(asMeeting(updated, { ...props.m, status, outcomeNote: note.trim() || null }));
      toast(`${props.m.businessName}: marked ${STATUS_LABEL[status].toLowerCase()}.`, 'ok');
      setOpen(false);
    } catch (e) {
      toast(`Couldn't update the meeting: ${errorMessage(e)}`, 'bad');
    } finally { setBusy(false); }
  };
  if (!open) return <button class="btn btn-sm" onClick={() => setOpen(true)}>Log outcome</button>;
  return (
    <div class="stack" style="gap:6px;min-width:260px">
      <textarea rows={2} value={note} placeholder="What happened (required for a no-show: did we try to reach them?)" onInput={(e) => setNote(e.currentTarget.value)} />
      <div class="row" style="gap:6px">
        <button class="btn btn-sm btn-primary" disabled={busy} onClick={() => void save('held')}>Held</button>
        <button class="btn btn-sm" disabled={busy || !note.trim()} title={!note.trim() ? 'Add a note first' : undefined} onClick={() => void save('no_show')}>No-show</button>
        <button class="btn btn-sm" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

/** One unlinked booking being linked: pick the business (the dialer's possible matches, or a search), the credit
 *  (default: the webhook's rules: the setter link's name, the lead's owner, the last caller, the first admin), Link. */
function LinkBooking(props: { b: CalendlyUnlinkedBooking; users: UserAdmin[] | null; onDone: () => void; onCancel: () => void }) {
  const [biz, setBiz] = useState<{ id: number; name: string } | null>(null);
  const [credit, setCredit] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const link = async () => {
    if (!biz) return;
    setBusy(true);
    setErr(null);
    try {
      const m = await endpoints.linkCalendly({ inviteeUri: props.b.inviteeUri, businessId: biz.id, ...(credit ? { setterId: credit } : {}) });
      toast(`Linked to ${m.businessName}, credited to ${m.setByName}.`, 'ok');
      props.onDone();
    } catch (e) {
      setErr(errorMessage(e));
    } finally { setBusy(false); }
  };
  return (
    <div class="stack" style="gap:8px">
      {!biz ? (
        <>
          {props.b.candidates.length ? (
            <div class="row" style="gap:6px;flex-wrap:wrap">
              <span class="muted" style="font-size:13px">Possible matches:</span>
              {props.b.candidates.map((c) => <button class="btn btn-sm" onClick={() => setBiz(c)}>{c.name} (#{c.id})</button>)}
            </div>
          ) : null}
          <LinkBusiness actionLabel="Pick" onPick={async (id, name) => { setBiz({ id, name }); }} onCancel={props.onCancel} />
        </>
      ) : (
        <div class="row" style="gap:8px;flex-wrap:wrap">
          <span>Link to <strong>{biz.name}</strong> (#{biz.id})</span>
          <button class="btn btn-sm" disabled={busy} onClick={() => setBiz(null)}>Change business</button>
          <label class="row" style="gap:6px">Credit
            <select value={String(credit)} disabled={busy} onChange={(e) => setCredit(e.currentTarget.value ? Number(e.currentTarget.value) : '')}>
              <option value="">Automatic (as the webhook would)</option>
              {(props.users ?? []).map((u) => <option value={String(u.id)}>{u.name}</option>)}
            </select>
          </label>
          <button class="btn btn-sm btn-primary" disabled={busy} onClick={() => void link()}>{busy ? 'Linking…' : 'Link'}</button>
          <button class="btn btn-sm" disabled={busy} onClick={props.onCancel}>Cancel</button>
        </div>
      )}
      <ErrorBanner error={err} />
    </div>
  );
}

/** Admins: the Calendly bookings the dialer hasn't linked (30 days), to clear by hand. Hidden when there are none. */
function CalendlyBacklog(props: { onLinked: () => void }) {
  const list = useAsync(() => endpoints.calendlyUnlinked({}), []);
  const [users, setUsers] = useState<UserAdmin[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    api.get<UserAdmin[]>('/api/users').then((u) => setUsers(Array.isArray(u) ? u.filter((x) => x.active) : []), () => setUsers([]));
  }, []);
  const bookings = list.data?.bookings ?? [];
  if (list.data && !bookings.length) return null;
  const now = Date.now();
  return (
    <div class="card" style="margin-bottom:14px">
      <div class="row" style="justify-content:space-between;gap:8px;flex-wrap:wrap">
        <h3 style="margin:0">Calendly bookings to link{list.data ? ` (${bookings.length})` : ''}</h3>
        <button class="btn btn-sm" onClick={list.reload}>Refresh</button>
      </div>
      <p class="vd-field-hint" style="margin:4px 0 8px">Booked on a Calendly link in the last 30 days, but the dialer couldn't tell which business (no single match by email, phone or business name). Pick the business and Link: it is recorded like any booking made on the link, prep task included.</p>
      <ErrorBanner error={list.error ? `Couldn't load the bookings: ${list.error}` : null} />
      {list.loading && !list.data ? <p class="muted"><Spinner /> Loading the bookings…</p> : null}
      {bookings.length ? (
        <div class="vd-table-wrap">
          <table>
            <thead><tr><th>Booked</th><th>Who</th><th>Meeting</th><th>Link</th><th></th></tr></thead>
            <tbody>
              {bookings.map((b) => [
                <tr>
                  <td style="white-space:nowrap">{fmtDateTime(b.receivedAt)}<div class="muted" style="font-size:12px">{bookedAgo(b.receivedAt, now)}</div></td>
                  <td>{bookingWho(b)}{b.inviteeEmail ? <div class="muted" style="font-size:13px">{b.inviteeEmail}{b.textNumber ? ` · ${b.textNumber}` : ''}</div> : null}
                    {b.detailsMissing ? <div class="vd-field-hint">Details not available from Calendly yet.</div> : null}</td>
                  <td style="white-space:nowrap">{b.startTime ? fmtDateTime(b.startTime) : '—'}</td>
                  <td>{b.linkName}</td>
                  <td>{open === b.inviteeUri ? null : <button class="btn btn-sm" onClick={() => setOpen(b.inviteeUri)}>Link…</button>}</td>
                </tr>,
                open === b.inviteeUri ? (
                  <tr class="vd-expanded"><td colSpan={5}>
                    <LinkBooking b={b} users={users} onCancel={() => setOpen(null)} onDone={() => { setOpen(null); list.reload(); props.onLinked(); }} />
                  </td></tr>
                ) : null,
              ])}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

/** The meeting a link opened (/meetings?open=<id>, e.g. from the Discord post), whatever the filters show. */
function OpenedMeeting(props: { id: number; admin: boolean; onClose: () => void }) {
  const d = useAsync<BookedCallDetail>(() => endpoints.bookedCall(props.id), [props.id]);
  const m = d.data?.meeting;
  return (
    <div class="card stack" style="gap:10px;margin-bottom:14px">
      <div class="row" style="justify-content:space-between;gap:8px;flex-wrap:wrap">
        <h3 style="margin:0">{m ? `${m.businessName}${m.city ? ` (${[m.city, m.stateProv].filter(Boolean).join(', ')})` : ''}` : `Meeting #${props.id}`}</h3>
        <button class="btn btn-sm" onClick={props.onClose}>Close</button>
      </div>
      {d.loading && !d.data ? <p class="muted"><Spinner /> Loading the meeting…</p> : null}
      <ErrorBanner error={d.error ? `Couldn't open meeting #${props.id}: ${d.error}` : null} />
      {m ? (
        <div class="muted" style="font-size:13px">
          {fmtDateTime(m.scheduledFor)} · set by {m.setterName}{m.campaignName ? ` · ${m.campaignName}` : ''} · {STATUS_LABEL[m.status]}{m.selfBooked ? ' · booked through the link' : ''}
        </div>
      ) : null}
      {d.data ? (d.data.bookedCall
        ? <BookedCallPanel meetingId={props.id} call={d.data.bookedCall} showDiscord canRetry={props.admin} />
        : <div class="muted">No booked call: this booking isn't tied to a call in the dialer.</div>) : null}
    </div>
  );
}

export function Meetings(_props: { params: Record<string, string> }) {
  const me = useMe();
  const openParam = Number(new URLSearchParams(location.search).get('open'));
  const [openId, setOpenId] = useState<number | null>(Number.isInteger(openParam) && openParam > 0 ? openParam : null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const closeOpened = () => { setOpenId(null); history.replaceState(null, '', '/meetings'); };
  const admin = me.role === 'admin';
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
  const [mine, setMine] = useState(!admin);
  const today = new Date();
  const [from, setFrom] = useState(isoDay(new Date(today.getTime() - 30 * 86400_000)));
  const [to, setTo] = useState(isoDay(new Date(today.getTime() + 30 * 86400_000)));
  const range = useAsync(() => endpoints.meetings({
    from: new Date(from + 'T00:00:00').toISOString(),
    to: new Date(to + 'T23:59:59').toISOString(),
    mine,
  }), [from, to, mine]);

  const all = range.data ?? [];
  const nowMs = Date.now();
  const upcoming = all.filter((m) => new Date(m.scheduledFor).getTime() >= nowMs).sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor));
  const past = all.filter((m) => new Date(m.scheduledFor).getTime() < nowMs).sort((a, b) => b.scheduledFor.localeCompare(a.scheduledFor));
  const list = tab === 'upcoming' ? upcoming : past;
  // A PATCH answers a plain Meeting: keep the booked call the list already has.
  const replace = (m: Meeting) => range.setData(all.map((x): MeetingWithBookedCall => (x.id === m.id ? { ...x, ...m, bookedCall: x.bookedCall } : x)));

  // Show rate by setter over the loaded range (held / (held + no-show)); meetings not yet logged are listed separately.
  const bySetter = new Map<string, { name: string; booked: number; held: number; noShow: number; unlogged: number }>();
  for (const m of all) {
    const row = bySetter.get(String(m.setBy)) ?? { name: m.setByName, booked: 0, held: 0, noShow: 0, unlogged: 0 };
    row.booked++;
    if (m.status === 'held') row.held++;
    else if (m.status === 'no_show') row.noShow++;
    else if (m.status === 'booked' && new Date(m.scheduledFor).getTime() < nowMs) row.unlogged++;
    bySetter.set(String(m.setBy), row);
  }

  const cancel = async (m: Meeting) => {
    try {
      const updated = await endpoints.updateMeeting(m.id, { status: 'canceled' });
      replace(asMeeting(updated, { ...m, status: 'canceled' }));
      toast(m.cancelUrl ? 'Marked canceled here. Use "Cancel in Calendly" too so they get the notice.' : 'Marked canceled.', 'info');
    } catch (e) {
      toast(`Couldn't cancel: ${errorMessage(e)}`, 'bad');
    }
  };

  return (
    <div style="max-width:1100px">
      <div class="vd-page-head">
        <div>
          <h1 style="margin:0">Meetings</h1>
          <p class="muted" style="margin:4px 0 0">Times in your time zone.</p>
        </div>
        <div class="row" style="gap:8px;flex-wrap:wrap">
          <Segmented value={tab} onChange={setTab} options={[{ value: 'upcoming', label: 'Upcoming', count: upcoming.length }, { value: 'past', label: 'Past', count: past.length }]} />
          {admin ? <Segmented value={mine ? 'mine' : 'all'} onChange={(v) => setMine(v === 'mine')} options={[{ value: 'mine', label: 'Mine' }, { value: 'all', label: 'Everyone' }]} /> : null}
        </div>
      </div>
      <div class="vd-filters">
        <label class="row" style="gap:6px">From <input type="date" value={from} onInput={(e) => e.currentTarget.value && setFrom(e.currentTarget.value)} /></label>
        <label class="row" style="gap:6px">To <input type="date" value={to} onInput={(e) => e.currentTarget.value && setTo(e.currentTarget.value)} /></label>
        <button class="btn btn-sm" onClick={range.reload}>Refresh</button>
      </div>
      {openId !== null ? <OpenedMeeting id={openId} admin={admin} onClose={closeOpened} /> : null}
      {admin ? <CalendlyBacklog onLinked={range.reload} /> : null}
      <ErrorBanner error={range.error ? `Couldn't load meetings: ${range.error}` : null} />
      {range.loading && !range.data ? <p class="muted"><Spinner /> Loading meetings…</p> : null}
      {range.data && list.length === 0 ? <Empty>{tab === 'upcoming' ? 'No upcoming meetings in this range.' : 'No past meetings in this range.'}</Empty> : null}

      {list.length ? (
        <div class="vd-table-wrap">
          <table>
            <thead><tr><th>When</th><th>Business</th><th>Invitee</th><th>Set by</th><th>Status</th><th>Call</th><th></th></tr></thead>
            <tbody>
              {list.map((m) => {
                const isPast = new Date(m.scheduledFor).getTime() < nowMs;
                const ownBooked = m.setBy === me.id && m.status === 'booked' && !isPast;
                const open = expanded === m.id;
                return [
                  <tr class={open ? 'vd-expanded' : ''}>
                    <td style="white-space:nowrap">{fmtDateTime(m.scheduledFor)}</td>
                    <td>{m.businessName}{m.outcomeNote ? <div class="vd-note">{m.outcomeNote}</div> : null}</td>
                    <td>{m.inviteeName ?? '—'}{m.inviteeEmail ? <div class="muted" style="font-size:13px">{m.inviteeEmail}</div> : null}</td>
                    <td>{m.setByName}</td>
                    <td><span class={`chip ${STATUS_TONE[m.status]}`}>{STATUS_LABEL[m.status]}</span></td>
                    <td style="white-space:nowrap">
                      {m.bookedCall
                        ? <button class="btn btn-sm" aria-expanded={open} onClick={() => setExpanded(open ? null : m.id)}>
                            {open ? 'Hide' : m.bookedCall.recording === 'stored' ? 'Listen + brief' : 'Call + brief'}
                          </button>
                        : <span class="muted" style="font-size:13px" title="No connected call by this setter to this business in the 14 days before the booking">—</span>}
                    </td>
                    <td>
                      <div class="row" style="gap:6px;flex-wrap:wrap;justify-content:flex-end">
                        {m.status === 'booked' && !isPast ? <LinkOut href={m.rescheduleUrl}>Reschedule in Calendly</LinkOut> : null}
                        {m.status === 'booked' && !isPast ? <LinkOut href={m.cancelUrl}>Cancel in Calendly</LinkOut> : null}
                        {admin && isPast && (m.status === 'booked' || m.status === 'held' || m.status === 'no_show') ? <Outcome m={m} onSaved={replace} /> : null}
                        {ownBooked || (admin && m.status === 'booked' && !isPast)
                          ? <ConfirmButton label="Mark canceled" confirmLabel="Yes, canceled" onConfirm={() => cancel(m)} /> : null}
                      </div>
                    </td>
                  </tr>,
                  open && m.bookedCall ? <tr class="vd-expanded"><td colSpan={7}><BookedCallPanel meetingId={m.id} call={m.bookedCall} showDiscord={admin} canRetry={admin} onChanged={range.reload} /></td></tr> : null,
                ];
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {bySetter.size ? (
        <div style="margin-top:20px;max-width:640px">
          <div class="eyebrow" style="margin-bottom:6px">Show rate by setter ({from} to {to})</div>
          <div class="vd-table-wrap">
            <table>
              <thead><tr><th>Setter</th><th>Booked</th><th>Held</th><th>No-show</th><th>Show rate</th><th>Not logged yet</th></tr></thead>
              <tbody>
                {[...bySetter.values()].sort((a, b) => b.booked - a.booked).map((r) => (
                  <tr>
                    <td>{r.name}</td><td>{r.booked}</td><td>{r.held}</td><td>{r.noShow}</td>
                    <td>{r.held + r.noShow ? pct(r.held / (r.held + r.noShow), 0) : 'not measured'}</td>
                    <td>{r.unlogged || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p class="vd-field-hint">Show rate counts only meetings logged held or no-show. Past meetings still "Booked" are not counted until an admin logs them.</p>
        </div>
      ) : null}
    </div>
  );
}
